import React, { FC } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Nature as NatureIcon,
  WbSunny,
} from '@mui/icons-material';
import { ProjectWithSites } from 'src/state-management/state';

export interface DeploymentImageItem {
  id: string;
  url: string;
  title: string;
  source: 'deployment_info' | 'retrieval_info';
  category: string;
  deploymentName?: string;
}

interface ProjectReadMoreDialogProps {
  open: boolean;
  onClose: () => void;
  projectDetails: ProjectWithSites;
  projectMedia?: any[];
  mediaUrls?: Record<string, string>;
  deploymentImages?: DeploymentImageItem[];
  isLoadingDeploymentImages?: boolean;
  onManageGalleryClick?: () => void;
}

const ProjectReadMoreDialog: FC<ProjectReadMoreDialogProps> = ({
  open,
  onClose,
  projectDetails,
  projectMedia = [],
  mediaUrls = {},
  deploymentImages = [],
  isLoadingDeploymentImages = false,
  onManageGalleryClick,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(8, 10, 14, 0.98)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          backdropFilter: 'blur(24px)',
          boxShadow: '0 32px 100px rgba(0,0,0,0.85)',
          color: '#fff',
          maxHeight: '88vh',
          overflow: 'hidden',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.72)' } },
      }}
    >
      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Breadcrumb bar */}
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 1.5,
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.35)',
              letterSpacing: '0.4px',
              fontWeight: 600,
            }}
          >
            PROJECTS &gt; {(projectDetails.project_name ?? '').toUpperCase().replace(/_/g, ' ')} &gt; OVERVIEW
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: 'rgba(255, 255, 255, 0.4)',
              '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.06)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box
          sx={{
            overflowY: 'auto',
            flex: 1,
            p: '24px 28px',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' },
          }}
        >
          {/* Project header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Typography sx={{ fontSize: '26px', fontWeight: 700, lineHeight: 1.25, mb: 1.5 }}>
                {(projectDetails.project_name ?? '').replace(/_/g, ' ')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    borderRadius: '6px',
                    px: 1.5,
                    py: '5px',
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      boxShadow: '0 0 6px #10b981',
                    }}
                  />
                  <Typography
                    sx={{ fontSize: '10px', fontWeight: 700, color: '#10b981', letterSpacing: '0.6px' }}
                  >
                    UNDER DEVELOPMENT
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>


          {/* Category tags */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
            {[
              { icon: <NatureIcon sx={{ fontSize: 14 }} />, label: 'Biodiversity' },
              { icon: <WbSunny sx={{ fontSize: 14 }} />, label: 'Climate' },
            ].map((tag) => (
              <Box
                key={tag.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.09)',
                  borderRadius: '20px',
                  px: 1.5,
                  py: '5px',
                }}
              >
                <Box sx={{ color: 'rgba(255, 255, 255, 0.55)' }}>{tag.icon}</Box>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                  {tag.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Metadata row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden',
              mb: 3,
            }}
          >
            {[
              { label: 'PROJECT DEVELOPER', value: (projectDetails as any).project_developer ?? 'FCF India' },
              { label: 'COUNTRY', value: (projectDetails as any).country ?? 'India' },
              {
                label: 'TOTAL AREA',
                value: (projectDetails as any).total_area
                  ? `${(Math.ceil(parseFloat((projectDetails as any).total_area) * 100) / 100).toFixed(2)} ha`
                  : '37.12 ha',
              },
            ].map((item, i) => (
              <Box
                key={item.label}
                sx={{
                  p: '14px 18px',
                  borderRight: i < 2 ? '1px solid rgba(255, 255, 255, 0.06)' : 'none',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '9px',
                    color: 'rgba(255, 255, 255, 0.35)',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    mb: 0.75,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Mission Intelligence */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#10b981', letterSpacing: '0.4px', mb: 1.5 }}>
              Mission Intelligence
            </Typography>
            <Typography sx={{ fontSize: '13px', lineHeight: 1.8, color: 'rgba(255, 255, 255, 0.72)' }}>
              {projectDetails.project_description ??
                `Current focus: ${(projectDetails.project_name ?? '').replace(
                  /_/g,
                  ' ',
                )} Analysis. Integrating multi-spectral satellite imagery with ground-level bioacoustic sensors to measure species return rates in degraded primary forest zones, generating verifiable carbon and biodiversity credits aligned with TNFD indicators.`}
            </Typography>
          </Box>


        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectReadMoreDialog;
